<template>
  <div class="configure-list-view__modal">
    <modal
      :active="true"
      :primary-button-disabled="processing"
      :primary-button-loading="processing"
      delete-button-label="Restore Defaults"
      tertiary-button-label="Cancel"
      primary-button-label="Apply"
      size="small"
      @onDelete="restoreDefaults()"
      @onClose="onClose()"
      @onConfirm="saveColumnConfig()">
      <template #header>
        <p>Configure Columns</p>
      </template>
      <template #content>
        <div class="configure-list-view__modal__body">
          <div class="configure-list-view__modal__body__left">
            <div class="configure-list-view__modal__body__left__header">
              Default Columns
            </div>
            <div
              v-for="(item, index) in defaultColumns"
              :key="index"
              class="configure-list-view__modal__body__left__title">
              <div
                v-if="getFormattedValue(item, 'isVisible', true)"
                class="configure-list-view__modal__body__left__title__tab">
                {{ item.title }}
              </div>
            </div>
          </div>
          <div class="configure-list-view__modal__body__right">
            <div class="configure-list-view__modal__body__right__header">
              Configuration
            </div>
            <div class="configure-list-view__modal__body__right__search">
              <text-input
                id="configure-search-input"
                placeholder="Search for Column"
                :value="searchTerm"
                :show-clear-button="true"
                icon="magnifying-glass"
                @onChange="onSearch($event)"
                @onClear="onClear" />
            </div>
            <div class="configure-list-view__modal__body__right__title">
              <draggable
                v-if="configColumns.length"
                :key="configColumns.length"
                :disabled="ifSearchTermIsNotEmpty"
                :list="configColumns">
                <div
                  v-for="item in configColumns"
                  :key="item.slotKey"
                  v-tooltip="getTooltip"
                  class="configure-list-view__modal__body__right__title__column">
                  <div
                    class="configure-list-view__modal__body__right__title__column__title">
                    <span
                      class="mi-20px mi-bars-horizontal"
                      aria-hidden="true"></span>
                    <span
                      class="configure-list-view__modal__body__right__title__column__title__value">
                      {{ item.title }}
                    </span>
                  </div>
                  <div
                    class="configure-list-view__modal__body__right__title__column__icons">
                    <span
                      v-if="
                        getFormattedValue(item, 'isFrozen', false) &&
                        getFormattedValue(item, 'isVisible', true)
                      "
                      class="mi-20px mi-lock"
                      :class="
                        getFormattedValue(item, 'isVisible', true)
                          ? 'configure-list-view__modal__body__right__title__column__icons__toggle-lock'
                          : 'configure-list-view__modal__body__right__title__column__icons__disabled'
                      "
                      aria-hidden="true"
                      @click="toggleIsFrozen(item)"></span>
                    <span
                      v-else
                      v-tooltip="
                        lockedNumber >= 5 && 'Cannot lock more than 5 columns'
                      "
                      class="mi-20px mi-lock-unlocked"
                      :class="
                        getFormattedValue(item, 'isVisible', true)
                          ? 'toggle-lock'
                          : 'disabled'
                      "
                      aria-hidden="true"
                      @click="toggleIsFrozen(item)"></span>
                    <span
                      v-if="getFormattedValue(item, 'isVisible', true)"
                      class="mi-24px mi-eye"
                      aria-hidden="true"
                      @click="toggleColumnVisibility(item)"></span>
                    <span
                      v-else
                      v-tooltip="
                        visibleNumber >= 35 &&
                        'Cannot show more than 35 columns'
                      "
                      class="mi-24px mi-eye-slash"
                      aria-hidden="true"
                      @click="toggleColumnVisibility(item)"></span>
                  </div>
                </div>
              </draggable>
              <div
                v-else
                class="configure-list-view__modal__body__right__title__no-items-found">
                <span>No Items found, please use correct column names.</span>
              </div>
            </div>
          </div>
        </div>
      </template>
    </modal>
  </div>
</template>

<script>
import draggable from "vuedraggable";
import { textSearchOnArrayOfObjectsBasedOnKey } from "@/utils/helpers.js";

export default {
  name: "ConfigureModal",
  components: {
    draggable,
  },
  props: {
    configColumns: {
      type: Array,
      default: () => [],
    },
    defaultColumns: {
      type: Array,
      default: () => [],
    },
    localStorageName: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      lockedNumber: 0,
      processing: false,
      visibleNumber: 0,
      searchTerm: "",
      configColumnsBeforeSearch: [],
    };
  },
  computed: {
    ifSearchTermIsNotEmpty() {
      return this.searchTerm.length ? true : false;
    },
    getTooltip() {
      return this.searchTerm.length
        ? "You cannot change the postion while searching for columns"
        : "";
    },
  },
  watch: {
    searchTerm() {
      if (!this.configColumnsBeforeSearch.length) {
        this.configColumnsBeforeSearch = JSON.parse(
          JSON.stringify(this.configColumns)
        );
      } else {
        this.configColumns.forEach((item) => {
          const searchedItemIndex = this.configColumnsBeforeSearch.findIndex(
            (elem) => elem.slotKey === item.slotKey
          );
          this.configColumnsBeforeSearch[searchedItemIndex] = item;
        });
      }
      this.configColumns = textSearchOnArrayOfObjectsBasedOnKey(
        JSON.parse(JSON.stringify(this.configColumnsBeforeSearch)),
        this.searchTerm,
        "title"
      );
    },
  },
  mounted() {
    let previousConfig = JSON.parse(
      localStorage.getItem(this.localStorageName)
    );
    this.configColumns = previousConfig
      ? previousConfig
      : JSON.parse(JSON.stringify(this.defaultColumns));
  },
  methods: {
    getFormattedValue(col, attrName, fallbackVal) {
      return Object.prototype.hasOwnProperty.call(col, attrName)
        ? col[attrName]
        : fallbackVal;
    },
    onClose() {
      this.$emit("onModalClose");
      this.searchTerm = "";
    },
    toggleColumnVisibility(item) {
      let count = 0;
      this.configColumns.forEach((item) => {
        if (this.getFormattedValue(item, "isVisible", true)) {
          count++;
        }
      });
      // this.visibleNumber = count;
      if (count < 35) {
        item.isVisible = !this.getFormattedValue(item, "isVisible", true);
      } else {
        item.isVisible = false;
      }
      count = 0;

      this.configColumns.forEach((item) => {
        if (this.getFormattedValue(item, "isVisible", true)) {
          count++;
        }
      });
      this.visibleNumber = count;
    },
    toggleIsFrozen(item) {
      let count = 0;
      this.configColumns.forEach((item) => {
        if (this.getFormattedValue(item, "isFrozen", false)) {
          count++;
        }
      });
      // this.lockedNumber = count;
      if (count < 5) {
        if (this.getFormattedValue(item, "isVisible", true)) {
          item.isFrozen = !this.getFormattedValue(item, "isFrozen", false);
          item.frozenPosition = "left";
        }
      } else {
        item.isFrozen = false;
      }
      count = 0;
      this.configColumns.forEach((item) => {
        if (this.getFormattedValue(item, "isFrozen", false)) {
          count++;
        }
      });
      this.lockedNumber = count;
    },
    saveColumnConfig() {
      this.processing = true;
      let updatedData = JSON.parse(JSON.stringify(this.configColumns));
      if (this.configColumnsBeforeSearch.length) {
        updatedData.forEach((item) => {
          const searchedItemIndex = this.configColumnsBeforeSearch.findIndex(
            (elem) => elem.slotKey === item.slotKey
          );
          this.configColumnsBeforeSearch[searchedItemIndex] = item;
        });
        updatedData = JSON.parse(
          JSON.stringify(this.configColumnsBeforeSearch)
        );
        this.configColumns = JSON.parse(
          JSON.stringify(this.configColumnsBeforeSearch)
        );
      }
      setTimeout(() => {
        // showToast("success", "Applying Column Configuration");
        this.$emit("saveColumnConfig", updatedData);
        localStorage.setItem(
          this.localStorageName,
          JSON.stringify(updatedData)
        );
        this.processing = false;
        this.$emit("onModalClose");
        this.searchTerm = "";
        this.configColumnsBeforeSearch = [];
      }, 10);
    },
    restoreDefaults() {
      this.configColumns = JSON.parse(JSON.stringify(this.defaultColumns));
    },
    onSearch(e) {
      this.searchTerm = e.target.value;
    },
    onClear() {
      this.searchTerm = "";
    },
  },
};
</script>

<style lang="scss">
.configure-list-view__modal {
  & .modal--small {
    width: 648px;
    height: 648px;
    & .modal__content--padding {
      padding: 0;
    }
    & .modal__footer__buttons__left--underline {
      color: var(--invert-color);
    }
  }
  &__body {
    display: flex;
    justify-content: space-between;
    overflow-y: auto;
    padding: 1.5rem;
    padding-top: 0;
    margin-bottom: 1.5rem;
    @media (min-width: 1025px) {
      max-height: 510px;
      margin-bottom: 0;
    }
    &__left {
      display: flex;
      flex-direction: column;
      box-shadow: 0px 3px 5px -1px var(--black-variant-1),
        0px 1px 18px var(--black-variant-2), 0px 6px 10px var(--black-variant-1);
      border-radius: 4px;
      background-color: var(--f-card-bg-color);
      max-width: 12rem;
      width: 100%;
      overflow-y: auto;
      cursor: default;
      @media (max-width: 1024px) {
        display: none;
      }
      &__header {
        font-family: "Maersk Text Bold";
        font-size: 14px;
        line-height: 16px;
        color: var(--mx-date-picker-calendar-color);
        font-weight: 700;
        padding-left: 24px;
        padding-top: 24px;
      }
      &__title {
        font-family: "Maersk Text";
        font-size: 14px;
        line-height: 16px;
        color: var(--mx-date-picker-calendar-color);
        &__tab {
          box-sizing: border-box;
          padding: 12px 24px;
          white-space: nowrap;
        }
      }
    }
    &__right {
      margin-left: 16px;
      background-color: var(--f-card-bg-color);
      overflow-y: auto;
      box-shadow: 0px 3px 5px -1px var(--black-variant-1),
        0px 1px 18px var(--black-variant-2), 0px 6px 10px var(--black-variant-1);
      border-radius: 4px;
      width: 100%;

      &__header {
        font-family: "Maersk Text Bold";
        font-size: 14px;
        line-height: 16px;
        color: var(--mx-date-picker-calendar-color);
        font-weight: 700;
        padding-left: 24px;
        padding-top: 24px;
      }

      &__search {
        margin: 12px 22px 0px 26px;

        & .form-input__field {
          background-color: var(--f-card-form-input-color);
        }
      }

      &__title {
        display: flex;
        flex-direction: column;

        &__no-items-found {
          display: flex;
          justify-content: center;
          margin-top: 1rem;
          padding: 1rem 2rem;
        }

        &__column {
          display: flex;
          align-items: center;
          justify-content: space-between;
          border: 1px solid var(--Gray-400);
          box-sizing: border-box;
          box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.1);
          margin: 12px 22px 0px 26px;
          padding: 8px 16px;

          &:last-child {
            margin-bottom: 15px;
          }

          &__icons {
            cursor: pointer;
            display: flex;
            align-content: center;

            & span:first-child {
              margin-right: 0.5rem;
            }

            &__toggle-lock {
              &:focus {
                outline: none;
              }
            }

            &__disabled {
              cursor: not-allowed;
            }
          }

          &__title {
            font-family: "Maersk Text";
            font-size: 14px;
            color: var(--mx-date-picker-calendar-color);
            cursor: pointer;
            display: flex;
            align-items: center;

            span {
              margin-right: 0.5rem;
            }
          }
        }
      }
    }
  }
}
</style>
